import { CheckmarkCircle, Close, CloseCircle, InformationCircle } from '@styled-icons/ionicons-outline';
import React from 'react';
import styled from 'styled-components';

import { EDataTestId } from '@webtrader/core/constants/test';
import { IWebAlertParams } from '@webtrader/core/services/alert';

import Portal from '@/components/Portal';

const Notification: FCC<IWebAlertParams> = (props) => {
  const { type, delay = 4000, title, message, onClose } = props;

  const notificationBoxRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const animationTimeout = setTimeout(() => {
      const notificationBox = notificationBoxRef.current;
      if (notificationBox) {
        notificationBox.style.animation = 'notificationClose 0.2s forwards';
      }
    }, delay);
    const closeTimeout = setTimeout(() => {
      onClose?.();
    }, delay + 250);
    return () => {
      clearTimeout(closeTimeout);
      clearTimeout(animationTimeout);
    };
  }, [delay, onClose]);

  const icon = React.useMemo(() => {
    switch (type) {
      case 'error':
        return <StyledErrorIcon size={25} />;
      case 'success':
        return <StyledSuccessIcon size={25} />;
      case 'info':
        return <StyledInfoIcon size={25} />;
      default:
        return null;
    }
  }, [type]);

  return (
    <StyledNotificationBox ref={notificationBoxRef} data-testid={EDataTestId.NOTIFICATION_BOX}>
      <StyledNotificationIcon>{icon}</StyledNotificationIcon>
      <StyledNotificationMessage>
        {title ? (
          <>
            <StyledNotificationMessageContainer>
              <StyledNotificationTitle data-testid={EDataTestId.NOTIFICATION_TITLE}>{title}</StyledNotificationTitle>
              <StyledIconContainer>
                <Close size={22} onClick={onClose} data-testid={EDataTestId.NOTIFICATION_CLOSE_BUTTON} />
              </StyledIconContainer>
            </StyledNotificationMessageContainer>
            {!!message && (
              <StyledNotificationDescription data-testid={EDataTestId.NOTIFICATION_DESCRIPTION}>
                {message}
              </StyledNotificationDescription>
            )}
          </>
        ) : (
          <StyledNotificationMessageContainer>
            <StyledNotificationDescription data-testid={EDataTestId.NOTIFICATION_DESCRIPTION}>
              {message}
            </StyledNotificationDescription>
            <StyledIconContainer>
              <Close size={22} onClick={onClose} data-testid={EDataTestId.NOTIFICATION_CLOSE_BUTTON} />
            </StyledIconContainer>
          </StyledNotificationMessageContainer>
        )}
      </StyledNotificationMessage>
    </StyledNotificationBox>
  );
};

export const showNotification = (props: IWebAlertParams) => {
  Portal((close) => <Notification {...props} onClose={close} />);
};

const StyledNotificationBox = styled.div`
  @keyframes notificationOpen {
    0% {
      opacity: 0;
      transform: translate(100%, 30%);
    }

    100% {
      opacity: 1;
      transform: translate(-8%, 30%);
    }
  }

  @keyframes notificationClose {
    0% {
      opacity: 1;
      transform: translate(-8%, 30%);
    }

    100% {
      opacity: 0;
      transform: translate(100%, 30%);
    }
  }

  position: absolute;
  top: 0;
  right: 0;
  z-index: 99;

  padding: 20px 16px;
  padding-inline-end: 20px;
  width: 300px;
  max-width: 300px;

  display: flex;
  align-items: flex-start;

  box-shadow: 0px 6px 14px rgba(10, 50, 90, 0.15);
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.background[0]};

  opacity: 1;
  transform: translate(100%, 30%);
  animation: notificationOpen 0.2s forwards;
  animation-iteration-count: 1;

  @media all and (max-width: 1280px) {
    width: 260px;
    max-width: 260px;

    padding: 18px 14px;
    padding-inline-end: 18px;
  }
`;

const StyledNotificationIcon = styled.div`
  min-width: 45px;

  @media all and (max-width: 1280px) {
    min-width: 30px;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

const StyledInfoIcon = styled(InformationCircle)`
  color: ${({ theme }) => theme.colors.tag.blue};
`;

const StyledErrorIcon = styled(CloseCircle)`
  color: ${({ theme }) => theme.colors.tag.error};
`;

const StyledSuccessIcon = styled(CheckmarkCircle)`
  color: ${({ theme }) => theme.colors.tag.success};
`;

const StyledNotificationMessage = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  @media all and (max-width: 1280px) {
    padding-bottom: 6px;
  }
`;

const StyledNotificationMessageContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledNotificationTitle = styled.div`
  color: ${({ theme }) => theme.colors.text[0]};
  font-size: 16.222px;

  svg {
    color: ${({ theme }) => theme.colors.text[1]};
    cursor: pointer;
  }

  @media all and (max-width: 1280px) {
    font-size: 14.5px;
  }
`;

const StyledNotificationDescription = styled.div`
  font-size: 13.222px;
  line-height: 20px;
  overflow-wrap: anywhere;

  @media all and (max-width: 1280px) {
    font-size: 11.222px;
    line-height: 18px;
  }
`;

const StyledIconContainer = styled.div`
  height: 22px;
  width: 22px;
  cursor: pointer;
`;
